import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { get } from 'lodash-es';
import { getMediaImageUrl } from 'client/utils/image-helpers';
import { ColorSwatch } from 'site-modules/shared/components/color-swatch/color-swatch';
import { ASPECT, getVinImage } from 'client/utils/inventory-image-helper';
import { getNoVehicleImageToDisplay } from 'site-modules/shared/utils/get-no-vehicle-image-to-display';
import { InventoryEntities } from 'client/data/models/inventory';
import { DATA_PATH } from 'client/engagement-handlers/inventory-engagement-handler/constants';
import { CardImage } from 'site-modules/shared/components/card-image/card-image';
import './similar-inventory-card.scss';

const IMAGE_WIDTH = 180;
const IMAGE_INDEX = 1;

/**
 * Gets color swatch component with predefined props.
 * @param {Object} color
 * @param {String} title
 * @param {String} [classes]
 * @returns {ReactElement}
 */
function getColorSwatch(color, title, classes) {
  const colorName = get(color, 'name', 'Not Available');
  return (
    <div className={classNames('d-inline-block', classes)}>
      <ColorSwatch color={color} displayColorName={false} title={`${title}: ${colorName}`} />
    </div>
  );
}

/**
 * Build image for inventory card
 * @param {String} vin
 * @return {*|String} image url
 */
export function buildVinImage(vin) {
  return getVinImage(vin, IMAGE_INDEX, IMAGE_WIDTH, ASPECT._16x7); // eslint-disable-line no-underscore-dangle
}

/**
 * Inventory card header.
 * @param {Object} vinData
 * @returns {ReactElement}
 */
export function SimilarInventoryCardHeader({ vinData, lazyLoadImage = false, colorDescribeId = null }) {
  const vin = get(vinData, 'vin', '');
  const isPhotoAvailable = !!get(vinData, 'vehicleInfo.photo.defaultPhoto.largePhotoCount', false);
  const stockPhotoUrl = get(vinData, DATA_PATH.STOCK_PHOTO_URL);
  const isStockPhoto = !!stockPhotoUrl;
  const exteriorColor = get(vinData, 'vehicleInfo.vehicleColors.exterior', null);
  const interiorColor = get(vinData, 'vehicleInfo.vehicleColors.interior', null);
  const image = isPhotoAvailable && getMediaImageUrl(buildVinImage(vin));

  const bodyType = get(vinData, 'vehicleInfo.styleInfo.bodyType');
  const noVehicleImage = getNoVehicleImageToDisplay(bodyType);
  const stockImage = isStockPhoto ? getMediaImageUrl(stockPhotoUrl) : noVehicleImage;

  const year = get(vinData, 'vehicleInfo.styleInfo.year', '');
  const make = get(vinData, 'vehicleInfo.styleInfo.make', '');
  const model = get(vinData, 'vehicleInfo.styleInfo.model', '');
  const mmy = `${year} ${make} ${model}`;

  return (
    <div className="pos-r m-0 inventory-card-photo-wrapper">
      <div className="d-block image-block mx-auto w-100">
        <CardImage
          className="image-header w-100 h-100 rounded-top"
          altText={mmy}
          photoUrl={image || stockImage}
          bodyType={bodyType}
          lazy={lazyLoadImage}
        />
      </div>
      {(exteriorColor || interiorColor) && (
        <div className="inventory-color-swatch mb-0_25 ms-0_25" id={colorDescribeId}>
          {exteriorColor && getColorSwatch(exteriorColor, 'Exterior', 'me-0_25')}
          {interiorColor && getColorSwatch(interiorColor, 'Interior')}
        </div>
      )}
      {isStockPhoto && <div className="inventory-stock-photo-label text-white disclaimer px-0_25">Stock Photo</div>}
    </div>
  );
}

SimilarInventoryCardHeader.propTypes = {
  vinData: InventoryEntities.InventoryVin.isRequired,
  lazyLoadImage: PropTypes.bool,
  colorDescribeId: PropTypes.string,
};
